/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/data_structures/KavenStack.ts
 * @create:      2019-03-21 16:20:59.274
 * @modify:      2025-07-01 13:13:47.371
 * @version:     6.0.0
 * @times:       30
 * @lines:       121
 * @copyright:   Copyright © 2019-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { ArrayLast } from "../array/ArrayLast";
import { IKavenStack } from "../interface/IKavenStack";

/**
 * Represents a variable size last-in-first-out (LIFO) collection of instances of the same specified type.
 * @since 1.1.19
 * @version 2021-12-14
 */
export class KavenStack<T> implements IKavenStack<T> {
    protected maxSize?: number;

    // Storage for stack elements
    protected array: T[];

    public constructor();
    public constructor(maxSize: number);
    public constructor(maxSize?: number) {
        this.maxSize = maxSize;
        this.array = [];
    }

    public get MaxSize() {
        return this.maxSize;
    }

    /**
     * Gets the number of elements contained in the KavenStack.
     * @returns The number of elements contained in the KavenStack.
     */
    public get Count() {
        return this.array.length;
    }

    /**
     * Removes all objects from the KavenStack.
     */
    public Clear(): void {
        // set to a new empty array,
        // we shouldn't have references to the original array.
        this.array = [];
    }

    /**
     * Determines whether an element is in the KavenStack.
     * @param item The object to locate in the KavenStack. The value can be undefined.
     */
    public Contains(item: T): boolean {
        return this.array.includes(item);
    }

    /**
     * Returns the object at the top of the KavenStack without removing it.
     * @returns The object at the top of the KavenStack.
     */
    public Peek(): T | undefined {
        return ArrayLast(this.array);
    }

    /**
     * Removes and returns the object at the top of the KavenStack.
     * @returns The object removed from the top of the KavenStack.
     */
    public Pop(): T | undefined {
        return this.array.pop();
    }

    /**
     * Inserts an object at the top of the KavenStack.
     * @param item The object to push onto the KavenStack. The value can be undefined.
     */
    public Push(item: T): void {
        this.array.push(item);

        if (this.MaxSize) {
            while (this.Count > this.MaxSize) {
                this.array.shift();
            }
        }
    }

    /**
     * Copies the KavenStack to a new array.
     * @returns A new array containing copies of the elements of the KavenStack.
     */
    public ToArray(): T[] {
        return this.array.slice(0);
    }
}
